import { useEffect, useState } from 'react';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useIsFirstRender from '../../../../../hooks/useIsFirstRender';
import storage from '../../../../../hooks/useStorage/useStorage';
import { useGetOfflineEncryptionKey } from '../../../../../services/modules/offline/offline.queries';
import { useNetworkContext } from '../../../contexts/network/network.context';
import { existsIndexDbToUser } from '../../../database/offlineDatabase/offlineDatabase';
import { offlineOrdersRepository } from '../../../repositories/offlineOrders.repository';
import { useOfflineDataDownloadInformation } from '../../useOfflineDataDownloadInformation';

interface UseHandleOfflineEncryptionKeyToCurrentTeamParams {
  currentTeamId?: string;
}

export const useHandleOfflineEncryptionKeyToCurrentTeam = ({
  currentTeamId,
}: UseHandleOfflineEncryptionKeyToCurrentTeamParams) => {
  const isFirstRender = useIsFirstRender();
  const { isOnline } = useNetworkContext();
  const { hasSyncedData, isSyncing } = useOfflineDataDownloadInformation();

  const [shouldGetKeyBecauseHaveOrders, setShouldGetKeyBecauseHaveOrders] = useState(false);

  const debouncedTeamId = useDebounce(currentTeamId, 1000);

  const shouldGetKey =
    (isSyncing || hasSyncedData) && !!debouncedTeamId && currentTeamId === debouncedTeamId;

  const shouldGetKeyConsideringOfflineOrders = shouldGetKey || shouldGetKeyBecauseHaveOrders;

  const { data: offlineEncryptionKey, isLoading } = useGetOfflineEncryptionKey({
    teamId: currentTeamId,
    options: {
      retry: true,
      staleTime: Infinity,
      enabled: shouldGetKeyConsideringOfflineOrders,
      retryDelay: (attempt) => attempt * 1000,
    },
  });

  const handleShouldGetKeyBecauseHaverOrders = async () => {
    const hasOfflineDatabase = await existsIndexDbToUser();

    if (!hasOfflineDatabase || shouldGetKey) {
      setShouldGetKeyBecauseHaveOrders(false);
      return;
    }

    const hasOfflineOrders = await offlineOrdersRepository.hasOrders();

    if (hasOfflineOrders) {
      setShouldGetKeyBecauseHaveOrders(true);
    } else {
      setShouldGetKeyBecauseHaveOrders(false);
    }
  };

  useEffect(() => {
    if (!isOnline || !debouncedTeamId) {
      return;
    }

    handleShouldGetKeyBecauseHaverOrders();
  }, [debouncedTeamId, isOnline, shouldGetKey]);

  useEffect(() => {
    if (
      !isOnline ||
      isLoading ||
      isFirstRender ||
      !currentTeamId ||
      !shouldGetKeyConsideringOfflineOrders
    ) {
      return;
    }

    if (offlineEncryptionKey) {
      storage.setOfflineEncryptionKey(currentTeamId, offlineEncryptionKey);
    }
  }, [currentTeamId, isLoading, offlineEncryptionKey, shouldGetKeyConsideringOfflineOrders]);

  useEffect(() => {
    if (!currentTeamId || !debouncedTeamId || !isOnline || isFirstRender) {
      return;
    }

    if (currentTeamId !== debouncedTeamId) {
      setShouldGetKeyBecauseHaveOrders(false);
      storage.removeOfflineEncryptionKey(debouncedTeamId);
    }
  }, [currentTeamId, debouncedTeamId]);
};
